import './styles.sass';

import React, { useMemo, useState } from 'react';

import { useJournalAnalysis } from './hooks';
import LoadingMessage from './LoadingMessage';
import ErrorMessage from './ErrorMessage';
import EmptyMessage from './EmptyMessage';
import AnalysisTable from './AnalysisTable';

import FilterSet from 'components/Explorer/models/filter_set';
import NumberHelper from 'util/number_helper';
import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';

import config from 'util/site_config';

const COLUMN_CONFIGURATION = [
  {
    name: I18n.t('Explorer.Journals.headers.journal_collection'),
    accessor: 'title',
    sortDirection: 'asc',
    alwaysVisible: true
  },
  {
    name: I18n.t('Explorer.Journals.headers.total_mentions'),
    accessor: 'total_mentions',
    type: 'number',
    alwaysVisible: true
  },
  { name: I18n.t('Explorer.Journals.headers.news_mentions'), accessor: 'msm', tab_id: 'msm', type: 'number' },
  { name: I18n.t('Explorer.Journals.headers.blog_mentions'), accessor: 'blog', tab_id: 'blogs', type: 'number' },
  { name: I18n.t('Explorer.Journals.headers.policy_mentions'), accessor: 'policy', tab_id: 'policy', type: 'number' },
  { name: I18n.t('Explorer.Journals.headers.patent_mentions'), accessor: 'patent', tab_id: 'patent', type: 'number' },
  { name: I18n.t('Explorer.Journals.headers.twitter_mentions'), accessor: 'tweet', tab_id: 'twitter', type: 'number' },
  {
    name: I18n.t('Explorer.Journals.headers.bluesky_mentions'),
    accessor: 'bluesky',
    tab_id: 'bluesky',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.peer_reviews_mentions'),
    accessor: 'peer_review',
    tab_id: 'peer_reviews',
    type: 'number'
  },
  { name: I18n.t('Explorer.Journals.headers.sina_weibo_mentions'), accessor: 'weibo', tab_id: 'weibo', type: 'number' },
  {
    name: I18n.t('Explorer.Journals.headers.facebook_mentions'),
    accessor: 'fbwall',
    tab_id: 'facebook',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.wikipedia_mentions'),
    accessor: 'wikipedia',
    tab_id: 'wikipedia',
    type: 'number'
  },
  { name: I18n.t('Explorer.Journals.headers.google_mentions'), accessor: 'gplus', tab_id: 'gplus', type: 'number' },
  {
    name: I18n.t('Explorer.Journals.headers.linked_in_mentions'),
    accessor: 'linkedin',
    tab_id: 'linkedin',
    type: 'number'
  },
  { name: I18n.t('Explorer.Journals.headers.reddit_mentions'), accessor: 'rdt', tab_id: 'reddit', type: 'number' },
  {
    name: I18n.t('Explorer.Journals.headers.pinterest_mentions'),
    accessor: 'pinterest',
    tab_id: 'pinterest',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.faculty_opinions_mentions'),
    accessor: 'f1000',
    tab_id: 'f1000',
    type: 'number'
  },
  { name: I18n.t('Explorer.Journals.headers.q_a_mentions'), accessor: 'qna', tab_id: 'qna', type: 'number' },
  { name: I18n.t('Explorer.Journals.headers.video_mentions'), accessor: 'video', tab_id: 'videos', type: 'number' },
  {
    name: I18n.t('Explorer.Journals.headers.syllabi_mentions'),
    accessor: 'syllabus',
    tab_id: 'syllabus',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.guideline_mentions'),
    accessor: 'guideline',
    tab_id: 'guideline',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.podcast_mentions'),
    accessor: 'podcast',
    tab_id: 'podcasts',
    type: 'number'
  },
  {
    name: I18n.t('Explorer.Journals.headers.seminar_mentions'),
    accessor: 'seminar',
    tab_id: 'seminars',
    type: 'number'
  }
];

const columnsForSources = (sources) =>
  COLUMN_CONFIGURATION.filter((column) => column.alwaysVisible || sources.includes(column.tab_id));

const JournalsAnalysisTable = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();
  const [mode, setMode] = useState('loading');

  const journalAnalysis = useJournalAnalysis(params, {
    onFetchStarted: () => setMode('loading'),
    onFetchSuccess: () => setMode('ok'),
    onFetchError: () => setMode('error')
  });

  const sortByMentionedResearchOutputs = useMemo(() => {
    const mentions = (row) => {
      if (!row?.original?.mentioned_research_outputs) return 0;
      return parseInt(row.original.mentioned_research_outputs);
    };
    return (rowA, rowB, desc) => {
      const [valA, valB] = [mentions(rowA), mentions(rowB)];
      if (desc) {
        if (valA > valB) return 1;
        if (valA < valB) return -1;
      } else {
        if (valA < valB) return -1;
        if (valA > valB) return 1;
      }
      return 0;
    };
  }, []);

  const goToJournal = (journalId) => {
    const filterSet = new FilterSet(params.filters);
    filterSet.set('journal_id[]', journalId);
    navigateParams(QueryParams.withFilters(params, filterSet.populatedFilters), { pathname: '/outputs' });
  };

  const data = useMemo(() => {
    return journalAnalysis.rows.map((row) => {
      const { mentioned_research_outputs } = row;
      return columnsForSources(config.sources).reduce(
        (result, { accessor }) => ({ ...result, [accessor]: row[accessor] }),
        { mentioned_research_outputs, source: row }
      );
    });
  }, [journalAnalysis.rows]);

  const columns = useMemo(() => {
    return columnsForSources(config.sources).map(({ name: Header, accessor, sortDirection }, index) => {
      const sortType = index === 0 ? sortByMentionedResearchOutputs : 'basic';
      const sortDescFirst = sortDirection !== 'asc';
      return {
        Header,
        accessor,
        sortType,
        sortDescFirst,
        Cell: ({ value }) => NumberHelper.formatNumberWithDelimiter(value)
      };
    });
  }, [sortByMentionedResearchOutputs]);

  const Messages = () => {
    if (mode === 'loading') return <LoadingMessage />;
    if (mode === 'error') return <ErrorMessage />;
    if (data.length < 1) return <EmptyMessage />;
    return null;
  };

  return (
    <div className="Explorer-Journals-Table-Container">
      <AnalysisTable {...{ columns, data, goToJournal, resultsPerPage: 50 }} />
      <Messages />
    </div>
  );
};

export default JournalsAnalysisTable;
